<script lang="ts">
  import RuleRow from './RuleRow.svelte';
  import type { Rule } from '../../types/rule';
  import { sumBy } from '../../lib/utils/array-helpers';

  let {
    rules = [],
    categories = [],
    people = [],
    editingRuleId = $bindable(null as string | null),
    editingRule = $bindable({} as Rule),
    loading = false,
    applyingRuleId = null as string | null,
    onEdit,
    onSave,
    onCancel,
    onToggleEnabled,
    onApply,
    onDelete,
    onKeydown
  }: {
    rules: Rule[];
    categories: string[];
    people: string[];
    editingRuleId?: string | null;
    editingRule?: Rule;
    loading?: boolean;
    applyingRuleId?: string | null;
    onEdit: (rule: Rule) => void;
    onSave: () => void;
    onCancel: () => void;
    onToggleEnabled: (rule: Rule) => void;
    onApply: (rule: Rule) => void;
    onDelete: (ruleId: string, pattern: string) => void;
    onKeydown: (event: KeyboardEvent) => void;
  } = $props();

  // Search/filter state
  let searchQuery = $state('');
  let expandedCategories = $state<Set<string>>(new Set());

  // Computed values
  const showGrouping = $derived(rules.length >= 5);
  const totalApplied = $derived(sumBy(rules, rule => rule.appliedCount || 0));

  // Filter rules based on search
  const filteredRules = $derived(() => {
    if (!searchQuery.trim()) return rules;

    const query = searchQuery.toLowerCase();
    return rules.filter(rule =>
      rule.pattern.toLowerCase().includes(query) ||
      rule.category.toLowerCase().includes(query) ||
      rule.person?.toLowerCase().includes(query) ||
      rule.normalizedName?.toLowerCase().includes(query)
    );
  });

  // Group rules by category
  const groupedRules = $derived(() => {
    if (!showGrouping) return null;

    const groups: Record<string, Rule[]> = {};
    const ungrouped: Rule[] = [];

    for (const rule of filteredRules()) {
      if (!groups[rule.category]) {
        groups[rule.category] = [];
      }
      groups[rule.category].push(rule);
    }

    // Separate single-rule categories from multi-rule categories
    const multiRuleGroups: Record<string, Rule[]> = {};
    for (const [category, rules] of Object.entries(groups)) {
      if (rules.length > 1) {
        multiRuleGroups[category] = rules;
      } else {
        ungrouped.push(rules[0]);
      }
    }

    return { grouped: multiRuleGroups, ungrouped };
  });

  function toggleCategory(category: string) {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    expandedCategories = newExpanded;
  }

  function expandAll() {
    if (!groupedRules()) return;
    expandedCategories = new Set(Object.keys(groupedRules()!.grouped));
  }

  function collapseAll() {
    expandedCategories = new Set();
  }
</script>

<div class="rules-list">
  <!-- Stats Summary -->
  <div class="stats-header">
    <h4>Active Rules ({rules.length})</h4>
    <div class="stats">
      <span class="stat-item">{totalApplied} transactions categorized</span>
    </div>
  </div>

  {#if rules.length === 0}
    <p class="empty">No rules yet. Add one above to automatically categorize transactions!</p>
  {:else}
    <!-- Search/Filter (only when 10+ rules) -->
    {#if showGrouping}
      <div class="search-controls">
        <input
          type="text"
          placeholder="Search rules by pattern, category, or person..."
          bind:value={searchQuery}
          class="search-input"
        />
        <div class="expand-controls">
          <button type="button" class="link-button" onclick={expandAll}>Expand All</button>
          <button type="button" class="link-button" onclick={collapseAll}>Collapse All</button>
        </div>
      </div>
    {/if}

    <div class="rules-table">
      <div class="table-header">
        <div class="col-pattern">Pattern</div>
        <div class="col-display">Display Name</div>
        <div class="col-category">Category</div>
        <div class="col-person">Person</div>
        <div class="col-flags">Flags</div>
        <div class="col-stats">Applied</div>
        <div class="col-enabled">Enabled</div>
        <div class="col-actions">Actions</div>
      </div>

      {#if groupedRules()}
        <!-- Multi-rule category groups -->
        {#each Object.entries(groupedRules()!.grouped) as [category, categoryRules] (category)}
          <div class="category-group">
            <button
              type="button"
              class="category-header"
              onclick={() => toggleCategory(category)}
            >
              <span class="expand-icon">{expandedCategories.has(category) ? '▼' : '▶'}</span>
              <span class="category-name">{category}</span>
              <span class="category-count">({categoryRules.length} rules)</span>
            </button>
            {#if expandedCategories.has(category)}
              {#each categoryRules as rule (rule.id)}
                <RuleRow
                  {rule}
                  {categories}
                  {people}
                  isEditing={editingRuleId === rule.id}
                  bind:editValues={editingRule}
                  {loading}
                  applyingRule={applyingRuleId === rule.id}
                  onEdit={() => onEdit(rule)}
                  onSave={onSave}
                  onCancel={onCancel}
                  onToggleEnabled={() => onToggleEnabled(rule)}
                  onApply={() => onApply(rule)}
                  onDelete={() => onDelete(rule.id, rule.pattern)}
                  {onKeydown}
                />
              {/each}
            {/if}
          </div>
        {/each}

        <!-- Single-rule categories (ungrouped) -->
        {#each groupedRules()!.ungrouped as rule (rule.id)}
          <RuleRow
            {rule}
            {categories}
            {people}
            isEditing={editingRuleId === rule.id}
            bind:editValues={editingRule}
            {loading}
            applyingRule={applyingRuleId === rule.id}
            onEdit={() => onEdit(rule)}
            onSave={onSave}
            onCancel={onCancel}
            onToggleEnabled={() => onToggleEnabled(rule)}
            onApply={() => onApply(rule)}
            onDelete={() => onDelete(rule.id, rule.pattern)}
            {onKeydown}
          />
        {/each}
      {:else}
        <!-- Flat view (< 10 rules) -->
        {#each filteredRules() as rule (rule.id)}
          <RuleRow
            {rule}
            {categories}
            {people}
            isEditing={editingRuleId === rule.id}
            bind:editValues={editingRule}
            {loading}
            applyingRule={applyingRuleId === rule.id}
            onEdit={() => onEdit(rule)}
            onSave={onSave}
            onCancel={onCancel}
            onToggleEnabled={() => onToggleEnabled(rule)}
            onApply={() => onApply(rule)}
            onDelete={() => onDelete(rule.id, rule.pattern)}
            {onKeydown}
          />
        {/each}
      {/if}
    </div>
  {/if}
</div>

<style lang="scss">
  .rules-list {
    .stats-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;

      h4 {
        font-size: var(--font-size-sm);
        margin: 0;
        color: var(--color-text-secondary);
        font-weight: var(--font-weight-semibold);
      }

      .stats {
        display: flex;
        gap: 15px;
        font-size: var(--font-size-sm);
        color: var(--color-text-tertiary);

        .stat-item {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
        }
      }
    }

    .empty {
      color: var(--color-text-tertiary);
      font-size: var(--font-size-sm);
      padding: var(--spacing-xl);
      text-align: center;
    }

    .search-controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--spacing-md);
      margin-bottom: 15px;

      .search-input {
        flex: 1;
        padding: var(--spacing-sm) var(--spacing-md);
        border: 1px solid var(--color-border-medium);
        border-radius: var(--radius-md);
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary);

        &:focus {
          outline: none;
          border-color: var(--color-primary-bright);
          box-shadow: var(--shadow-focus);
        }

        &::placeholder {
          color: var(--color-text-muted);
        }
      }

      .expand-controls {
        display: flex;
        gap: var(--spacing-md);

        .link-button {
          background: none;
          border: none;
          color: var(--color-primary-bright);
          font-size: var(--font-size-xs);
          cursor: pointer;
          padding: var(--spacing-xs) 8px;
          border-radius: var(--spacing-xs);
          transition: background-color 0.2s;

          &:hover {
            background-color: var(--color-primary-light);
          }
        }
      }
    }
  }

  .rules-table {
    .table-header {
      display: grid;
      grid-template-columns: 1.5fr 1.5fr 1.2fr 0.8fr 1fr 0.6fr 0.6fr 2fr;
      gap: var(--spacing-md);
      align-items: center;
      padding: var(--spacing-md);
      font-size: var(--font-size-xxs);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-tertiary);
      border-bottom: 2px solid var(--color-border);
      text-transform: uppercase;
    }

    .category-group {
      border-bottom: 1px solid var(--color-background-tertiary);

      .category-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        width: 100%;
        padding: var(--spacing-md) 12px;
        background-color: var(--color-background-secondary);
        border: none;
        border-top: 1px solid var(--color-border);
        cursor: pointer;
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-semibold);
        color: var(--color-text-secondary);
        text-align: left;
        transition: background-color 0.2s;

        &:hover {
          background-color: var(--color-background-tertiary);
        }

        .expand-icon {
          font-size: var(--font-size-xxs);
          color: var(--color-text-tertiary);
          width: 12px;
        }

        .category-name {
          flex: 1;
        }

        .category-count {
          font-size: var(--font-size-xs);
          color: var(--color-text-tertiary);
          font-weight: var(--font-weight-medium);
        }
      }
    }
  }
</style>
